import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  getNudgeUnreadCount,
  listNudgeInbox,
  type NudgeInboxResponse,
  type NudgeUnreadCount,
} from "@ando/core";

/**
 * Shared TanStack Query keys + hooks for the Wave-4 proactive-layer
 * inbox. Centralised here (May 31, 2026) so the bell popover AND the
 * ``/workspace/work`` "Notifiche di Ando" section read from the SAME
 * cache — no duplicate fetch per page, single invalidation point on
 * ``nudge_received`` WS frames.
 *
 * Key shape:
 *   - ``["nudges", "unread-count"]``        → badge count
 *   - ``["nudges", "inbox", { limit }]``    → list, per-limit cache slot
 *
 * Defaults are deliberately conservative: badge polls every 5min (matches
 * v1 cadence); inbox list is on-demand (``enabled`` opt-in for surfaces
 * that don't always render it, e.g. the popover only fetches when open).
 */

export const NUDGE_UNREAD_QUERY_KEY = ["nudges", "unread-count"] as const;
export const NUDGE_INBOX_QUERY_KEY = ["nudges", "inbox"] as const;
const NUDGE_BADGE_POLL_MS = 5 * 60_000;
const NUDGE_INBOX_STALE_MS = 30_000;

export interface UseNudgeInboxOptions {
  /** Max items to fetch — defaults to 8 (matches the bell popover). */
  readonly limit?: number;
  /** Cache-gate (e.g. only fetch when the popover is open). */
  readonly enabled?: boolean;
}

export function useNudgeUnreadCount(): UseQueryResult<NudgeUnreadCount, Error> {
  return useQuery({
    queryKey: NUDGE_UNREAD_QUERY_KEY,
    queryFn: () => getNudgeUnreadCount(),
    staleTime: 60_000,
    refetchInterval: NUDGE_BADGE_POLL_MS,
    // Badge is informational — a failed fetch must not surface a toast.
    retry: 1,
  });
}

export function useNudgeInbox(
  opts: UseNudgeInboxOptions = {},
): UseQueryResult<NudgeInboxResponse, Error> {
  const limit = opts.limit ?? 8;
  return useQuery({
    queryKey: [...NUDGE_INBOX_QUERY_KEY, { limit }],
    queryFn: () => listNudgeInbox({ limit }),
    enabled: opts.enabled ?? true,
    staleTime: NUDGE_INBOX_STALE_MS,
    retry: 1,
  });
}
